import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router';
import { useMutation } from '@apollo/client';
import {
  Backdrop,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Cancel } from '@material-ui/icons';

import { Container as DrawerContainer, Footer as DrawerFooter } from '../Drawer';
import {
  COLOR_BORDER,
  COLOR_BUTTON_BACKGROUND,
  COLOR_BUTTON_TEXT,
  COLOR_DISABLE_BACKGROUND,
  COLOR_EFFECT,
  COLOR_TEXT_DISABLE,
  COLOR_TEXT_MAIN,
} from '../../../../theme/colors';
import LoadingButton from '../../primitive/LoadingButton';
import globalState from '../../../../operations/commands';
import { CreateTopicDocument, CreateTopicMutation, CreateTopicMutationVariables } from '../../../../generated/graphql';
import { useUploadAttachmentFile } from '../../../../hooks/useUploadAttachmentFile';
import { MAX_MESSAGE_LENGTH, MAX_TOPIC_LENGTH } from '../../../constant/limitedLength';
import mutationVariables from '../../../../utility/mutationVariables';

type Props = {
  refetch: () => void;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

type Params = {
  experiment_id: string;
};

/**
 * ファイルを追加する
 */
export const NewThreadDrawer: FC<Props> = ({ refetch, open, setOpen }) => {
  const { experiment_id } = useParams<Params>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [urls, setUrls] = useState<string[]>([]);
  const { uploadFile } = useUploadAttachmentFile('topics');

  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      if (50 * 1000 ** 2 < e?.target?.files[0].size) {
        globalState.setErrors({
          message: 'ファイルの最大サイズを超えています。',
          name: 'UnimportantError',
          context: 'planbcd',
        });
        return;
      }

      setFiles([...files, e.target.files[0]]);

      const newUrls = [...urls];
      newUrls.push(URL.createObjectURL(e.target.files[0]));
      setUrls(newUrls);
    }
  };

  const deleteFile = (index: number) => {
    const newFiles = [...files];
    const newUrls = [...urls];

    newFiles.splice(index, 1);
    newUrls.splice(index, 1);
    setFiles(newFiles);
    setUrls(newUrls);
  };

  const [createTopic, { loading }] = useMutation<CreateTopicMutation, CreateTopicMutationVariables>(
    CreateTopicDocument,
    {
      context: { clientName: 'planbcd' },
      onCompleted: (data: CreateTopicMutation): void => {
        files.map(async (file) => {
          await uploadFile(file, data.createTopic.id);
        });
        setFiles([]);
        setUrls([]);
        setTopic('');
        setMessage('');
        setOpen(false);
        setTimeout(() => {
          refetch();
        }, 2000);
      },
      onError: (error) => {
        console.error(error);
        globalState.setErrors({
          message: 'error',
          name: 'NonFatalError',
          context: { clientName: 'planbcd' },
        });
      },
    },
  );

  /**
   * トピック作成の実行
   */
  const confirmPost = () => {
    if (!experiment_id) {
      globalState.setErrors({
        message: 'トピックの作成ができません',
        name: 'NonFatalError',
        context: 'planbcd',
      });
      return;
    }
    createTopic(
      mutationVariables({
        project_id: experiment_id!,
        project_topic: {
          title: topic,
          comment: message,
        },
      }),
    );
  };

  return (
    <>
      <Backdrop open={loading} style={{ zIndex: 20000, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* ファイルを追加する */}
      <Drawer
        anchor="right"
        open={open}
        onClose={(_, reason: any) => {
          if (reason !== 'backdropClick') {
            reason;
          }
        }}
      >
        <DrawerContainer>
          <DrawerContent>
            <Header>
              <Title>トピックを作成する</Title>
            </Header>
            <Paragraph>
              {
                '・投稿された内容は、このページを閲覧できるユーザー全員に共有されます。\n・不適切な内容の場合、サポートスタッフにより削除させていただくこともございます。'
              }
            </Paragraph>
            <Content>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <LabelWrapper>
                    <InputLabel>{'トピック'}</InputLabel>
                    <RequiredLabel>必須</RequiredLabel>
                  </LabelWrapper>
                  <StyledInputField
                    variant={`outlined`}
                    value={topic}
                    onChange={(e) => {
                      if (e.target.value.length > MAX_TOPIC_LENGTH) {
                        return;
                      }
                      setTopic(e.target.value);
                    }}
                    placeholder="トピック名を入力してください"
                  />
                  <ThreadMessageLabel>{`${topic.length} / ${MAX_TOPIC_LENGTH}`}</ThreadMessageLabel>
                </Grid>
                <Grid item xs={12}>
                  <LabelWrapper>
                    <InputLabel>{'メッセージ'}</InputLabel>
                    <RequiredLabel>必須</RequiredLabel>
                  </LabelWrapper>
                  <CommentField
                    variant={`outlined`}
                    multiline
                    rows={5}
                    value={message}
                    onChange={(e) => {
                      if (e.target.value.length > MAX_MESSAGE_LENGTH) {
                        return;
                      }
                      setMessage(e.target.value);
                    }}
                    placeholder="メッセージを入力してください"
                  />
                  <ThreadMessageLabel>{`${message.length} / ${MAX_MESSAGE_LENGTH}`}</ThreadMessageLabel>
                </Grid>
                <Grid item xs={12}>
                  <FileUploadButtonWrapper>
                    <SecondaryButton
                      onClick={() => {
                        if (inputRef?.current) {
                          inputRef.current.click();
                        }
                      }}
                      disabled={files.length >= 3}
                    >
                      アップロードする素材・資料を選択する
                    </SecondaryButton>
                    <FileLimitLabel>{'最大50MB / 最大3ファイル'}</FileLimitLabel>
                  </FileUploadButtonWrapper>
                </Grid>
                <Grid item xs={12}>
                  <FileUploadWrapper>
                    {files.length > 0 && urls.length > 0
                      ? urls.map((url, index) =>
                          files[index] ? (
                            files[index].type === 'image/jpeg' || files[index].type === 'image/png' ? (
                              <UploadedImageFileWrapper key={url}>
                                <UploadedFile src={url} alt={files[index].name} />
                                <UploadedImageFileDeleteButton
                                  onClick={() => {
                                    deleteFile(index);
                                  }}
                                >
                                  <Cancel />
                                </UploadedImageFileDeleteButton>
                              </UploadedImageFileWrapper>
                            ) : (
                              <UploadedFileWrapper key={url}>
                                <div>{files[index].name}</div>
                                <UploadedFileDeleteButton
                                  onClick={() => {
                                    deleteFile(index);
                                  }}
                                >
                                  <Cancel />
                                </UploadedFileDeleteButton>
                              </UploadedFileWrapper>
                            )
                          ) : null,
                        )
                      : null}
                  </FileUploadWrapper>
                </Grid>
              </Grid>
              <input
                hidden
                id="img"
                type="file"
                ref={inputRef}
                accept="*/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
              />
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <SecondaryButton
              onClick={() => {
                setFiles([]);
                setUrls([]);
                setOpen(false);
              }}
            >
              キャンセル
            </SecondaryButton>
            <PrimaryButton
              loading={false}
              disabled={topic.length === 0 || message.length === 0}
              onClick={() => {
                confirmPost();
              }}
            >
              保存する
            </PrimaryButton>
          </DrawerFooter>
        </DrawerContainer>
      </Drawer>
    </>
  );
};

const CommentField = withStyles({
  root: {
    width: '100%',
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {
      color: COLOR_TEXT_MAIN,
      borderRadius: '8px',
      fontSize: '14px',
      height: '118px',
      '& fieldset': {},
      '&.Mui-error fieldset': {
        border: '1.5px solid #FE7877',
      },
      '&.Mui-error input': {
        color: '#FE7877',
      },
    },
  },
})(TextField);

const StyledInputField = withStyles({
  root: {
    width: '100%',
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {
      color: COLOR_TEXT_MAIN,
      borderRadius: '8px',
      fontSize: '14px',
      height: '40px',
      '& fieldset': {},
      '&.Mui-error fieldset': {
        border: '1.5px solid #FE7877',
      },
      '&.Mui-error input': {
        color: '#FE7877',
      },
    },
  },
})(TextField);

const Header = styled('div')(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`);

const Content = styled('div')(`
  height: calc(100% - 64px);
  width: 100%;
  padding-top: 24px;
`);

const Title = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
`);

const Paragraph = styled('div')(`
  font-size: 14px;
  line-height: 160%;
  white-space: pre-wrap;
  color: #4C566C;
`);

const PrimaryButton = styled(LoadingButton)(`
  color: ${COLOR_BUTTON_TEXT};
  height: 40px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: ${COLOR_EFFECT};
    border-color: ${COLOR_EFFECT};
  }
  :disabled {
    background: ${COLOR_DISABLE_BACKGROUND};
    color: ${COLOR_TEXT_DISABLE};
    border: ${COLOR_TEXT_DISABLE};
  }
`);

const SecondaryButton = styled(Button)<{ width?: string }>(`
  color: ${COLOR_TEXT_MAIN};
  height: 40px;
  padding: 13px 16px;
  font-size: 14px;
  background: ${COLOR_BUTTON_BACKGROUND};
  font-weight: 700;
  border: 1.5px solid ${COLOR_BORDER};
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: ${COLOR_BUTTON_BACKGROUND};
    color: ${COLOR_EFFECT};
  }
`);

const DrawerContent = styled('div')(`
  padding: 40px 40px;
  width: 864px;
  height: calc(100% - 65px);
  position: relative;
  overflow-y: auto;
  fieldset {
    outline: none;
    border: 1.5px solid ${COLOR_BORDER};
  }
  .MuiOutlinedInput-root:hover fieldset {
    outline: none;
    border: 1.5px solid ${COLOR_BORDER};
  }
  .MuiOutlinedInput-root.Mui-focused {
    fieldset {
      outline: none;
      border: 1.5px solid ${COLOR_EFFECT};
    }
  }
`);

const LabelWrapper = styled('div')`
  display: flex;
`;

const InputLabel = styled(Typography)`
  font-size: 12px;
  color: #4c566c;
  margin-right: 4px;
  margin-bottom: 8px;
`;

const RequiredLabel = styled(Typography)`
  color: #bf4140;
  font-size: 10px;
`;

const FileUploadWrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const FileUploadButtonWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const UploadedImageFileWrapper = styled('div')`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const UploadedFileWrapper = styled('div')`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;

const UploadedImageFileDeleteButton = styled(IconButton)`
  margin-left: -48px;
`;

const UploadedFileDeleteButton = styled(IconButton)``;

const UploadedFile = styled('img')`
  max-height: 180px;
  max-width: 240px;
  border-radius: 4px;
  box-shadow: 0px 1px 9px rgb(0 0 0 / 15%);
`;

const FileLimitLabel = styled(Typography)`
  font-size: 12px;
  color: #4c566c;
`;

const ThreadMessageLabel = styled(Typography)`
  color: #4c566c;
  font-size: 10px;
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
`;
